import React, { useState } from 'react';
import { ObjectCategory } from '../types/labelSchema';

interface CategoryManagerProps {
  // Subcategories (existing collection)
  categories: ObjectCategory[];
  loading: boolean;
  error: string | null;
  onCreateCategory: (data: { name: string; displayName: string; description?: string; color: string; parentCategoryId?: string }) => Promise<string>;
  onUpdateCategory: (id: string, updates: Partial<ObjectCategory>) => Promise<void>;
  onDeleteCategory: (id: string) => Promise<void>;
  // Parent categories (new collection)
  parentCategories: ObjectCategory[];
  parentLoading: boolean;
  parentError: string | null;
  onCreateParent: (data: { name: string; displayName: string; description?: string; color: string }) => Promise<string>;
  onUpdateParent: (id: string, updates: Partial<ObjectCategory>) => Promise<void>;
  onDeleteParent: (id: string) => Promise<void>;
  onClose: () => void;
}

export default function CategoryManager({
  categories,
  loading,
  error,
  onCreateCategory,
  onUpdateCategory,
  onDeleteCategory,
  parentCategories,
  parentLoading,
  parentError,
  onCreateParent,
  onUpdateParent,
  onDeleteParent,
  onClose
}: CategoryManagerProps) {
  const [activeTab, setActiveTab] = useState<'parents' | 'subcategories'>('parents');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ObjectCategory | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    color: '#3b82f6',
    parentCategoryId: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const predefinedColors = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6b7280'
  ];

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.displayName) {
      alert('Please fill in all required fields');
      return;
    }
    
    setIsSubmitting(true);
    try {
      if (activeTab === 'parents') {
        await onCreateParent(formData);
      } else {
      await onCreateCategory(formData);
      }
      setFormData({ name: '', displayName: '', description: '', color: '#3b82f6', parentCategoryId: '' });
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Failed to create category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingCategory || !formData.name || !formData.displayName) {
      alert('Please fill in all required fields');
      return;
    }
    
    setIsSubmitting(true);
    try {
      if (activeTab === 'parents') {
        await onUpdateParent(editingCategory.id, formData);
      } else {
      await onUpdateCategory(editingCategory.id, formData);
      }
      setEditingCategory(null);
      setFormData({ name: '', displayName: '', description: '', color: '#3b82f6', parentCategoryId: '' });
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCategory = async (category: ObjectCategory, isParent = false) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the ${isParent ? 'parent category' : 'subcategory'} "${category.displayName}"?\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    try {
      if (isParent) {
        await onDeleteParent(category.id);
      } else {
      await onDeleteCategory(category.id);
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    }
  };

  const startEdit = (category: ObjectCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      displayName: category.displayName,
      description: category.description || '',
      color: category.color,
      parentCategoryId: category.parentCategoryId || ''
    });
  };

  const cancelEdit = () => {
    setEditingCategory(null);
    setFormData({ name: '', displayName: '', description: '', color: '#3b82f6', parentCategoryId: '' });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Manage Categories & Subcategories</h2>
              <p className="text-sm text-gray-600">Top-level Categories and linked Subcategories</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="mb-4 flex space-x-4 border-b border-gray-200">
            {[
              { id: 'parents', label: 'Categories (parent)' },
              { id: 'subcategories', label: 'Subcategories' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id as 'parents' | 'subcategories'); setShowCreateForm(false); setEditingCategory(null); }}
                className={`px-4 py-2 -mb-px border-b-2 ${activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Create/Edit Form */}
          {(showCreateForm || editingCategory) && (
            <div className="mb-6 p-4 border border-gray-200 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">
                {editingCategory ? `Edit ${activeTab === 'parents' ? 'Category' : 'Subcategory'}` : `Create New ${activeTab === 'parents' ? 'Category' : 'Subcategory'}`}
              </h3>
              
              <form onSubmit={editingCategory ? handleUpdateCategory : handleCreateCategory} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {activeTab === 'parents' ? 'Category ID *' : 'Subcategory ID *'}
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={activeTab === 'parents' ? 'e.g., kitchen' : 'e.g., kitchen_utensils'}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Internal ID (lowercase, underscores)</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Name *
                    </label>
                    <input
                      type="text"
                      value={formData.displayName}
                      onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Kitchen Utensils"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`Brief description of this ${activeTab === 'parents' ? 'category' : 'subcategory'}`}
                  />
                </div>

                {activeTab === 'subcategories' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Parent Category
                    </label>
                    <select
                      value={formData.parentCategoryId}
                      onChange={(e) => setFormData({ ...formData, parentCategoryId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">None (Unassigned)</option>
                      {parentCategories.map((parent) => (
                        <option key={parent.id} value={parent.id}>
                          {parent.displayName}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Optional. Assign to a parent category.</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {predefinedColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData({ ...formData, color })}
                        className={`w-8 h-8 rounded-full border-2 ${
                          formData.color === color ? 'border-gray-400' : 'border-gray-200'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={editingCategory ? cancelEdit : () => setShowCreateForm(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      isSubmitting 
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isSubmitting ? 'Saving...' : (editingCategory ? 'Update Category' : 'Create Category')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Categories/Subcategories List */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                {activeTab === 'parents' ? `Categories (${parentCategories.length})` : `Subcategories (${categories.length})`}
              </h3>
              {!showCreateForm && !editingCategory && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {activeTab === 'parents' ? 'Create Category' : 'Create Subcategory'}
                </button>
              )}
            </div>

            {activeTab === 'parents' ? (
              parentLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : parentError ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600">{parentError}</p>
                </div>
              ) : parentCategories.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">No parent categories found. Create your first category to get started.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {parentCategories.map((category) => (
                    <div key={category.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full border-2 border-gray-300"
                          style={{ backgroundColor: category.color }}
                        ></div>
                        <div>
                          <h4 className="font-medium text-gray-900">{category.displayName}</h4>
                          <p className="text-sm text-gray-600">{category.name}</p>
                          {category.description && (
                            <p className="text-xs text-gray-500">{category.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => { setActiveTab('parents'); startEdit(category); }}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category, true)}
                          className="px-3 py-1 text-sm bg-red-200 text-red-700 rounded hover:bg-red-300 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600">{error}</p>
              </div>
            ) : categories.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No subcategories found. Create your first subcategory to get started.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {categories.map((category) => (
                  <div key={category.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-4 h-4 rounded-full border-2 border-gray-300"
                        style={{ backgroundColor: category.color }}
                      ></div>
                      <div>
                        <h4 className="font-medium text-gray-900">{category.displayName}</h4>
                        <p className="text-sm text-gray-600">{category.name}</p>
                        {category.parentCategoryId && (
                          <p className="text-xs text-gray-500">
                            Parent: {parentCategories.find(p => p.id === category.parentCategoryId)?.displayName || category.parentCategoryId}
                          </p>
                        )}
                        {category.description && (
                          <p className="text-xs text-gray-500">{category.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => { setActiveTab('subcategories'); startEdit(category); }}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(category)}
                        className="px-3 py-1 text-sm bg-red-200 text-red-700 rounded hover:bg-red-300 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

